'use client'

import { useCallback, useEffect, useState } from 'react'
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api'
import type { Account, ContactLabel } from '@/types'

type UseLabelsResult = {
  labels: ContactLabel[]
  loading: boolean
  refreshing: boolean
  reload: () => Promise<void>
  /** Ask the server to pull the canonical list from Go and overwrite the cache. */
  refresh: () => Promise<void>
  create: (name: string) => Promise<ContactLabel[]>
  rename: (id: number, name: string) => Promise<ContactLabel[]>
  remove: (id: number) => Promise<ContactLabel[]>
}

/**
 * Labels for the active account. Refresh/create/remove sync through wxproto;
 * rename stays local because protocol ModifyLabel changes membership, not names.
 */
export function useLabels(account: Account | null): UseLabelsResult {
  const [labels, setLabels] = useState<ContactLabel[]>([])
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const reload = useCallback(async () => {
    if (!account) {
      setLabels([])
      return
    }
    setLoading(true)
    try {
      const qs = 'wxid=' + encodeURIComponent(account.wxid)
      const response = await apiGet<{ success: boolean; labels: ContactLabel[] }>(`/bot/labels?${qs}`)
      setLabels(response.labels || [])
    } finally {
      setLoading(false)
    }
  }, [account])

  const refresh = useCallback(async () => {
    if (!account) return
    setRefreshing(true)
    try {
      const response = await apiPost<{ success: boolean; labels: ContactLabel[] }>('/bot/labels/refresh', {
        wxid: account.wxid,
      })
      setLabels(response.labels || [])
    } finally {
      setRefreshing(false)
    }
  }, [account])

  const create = useCallback(
    async (name: string) => {
      if (!account) return []
      setRefreshing(true)
      try {
        const response = await apiPost<{ success: boolean; labels: ContactLabel[] }>('/bot/labels', {
          wxid: account.wxid,
          name,
        })
        const next = response.labels || []
        setLabels(next)
        return next
      } finally {
        setRefreshing(false)
      }
    },
    [account],
  )

  const rename = useCallback(
    async (id: number, name: string) => {
      if (!account) return []
      setRefreshing(true)
      try {
        const response = await apiPatch<{ success: boolean; labels: ContactLabel[] }>(
          `/bot/labels/${id}?wxid=${encodeURIComponent(account.wxid)}`,
          { name },
        )
        const next = response.labels || []
        setLabels(next)
        return next
      } finally {
        setRefreshing(false)
      }
    },
    [account],
  )

  const remove = useCallback(
    async (id: number) => {
      if (!account) return []
      setRefreshing(true)
      try {
        const response = await apiDelete<{ success: boolean; labels: ContactLabel[] }>(
          `/bot/labels/${id}?wxid=${encodeURIComponent(account.wxid)}`,
        )
        const next = response.labels || []
        setLabels(next)
        return next
      } finally {
        setRefreshing(false)
      }
    },
    [account],
  )

  useEffect(() => {
    void reload()
  }, [reload])

  return { labels, loading, refreshing, reload, refresh, create, rename, remove }
}
